import React from "react";

const errorMessage = (error) => error?.message || String(error || "Unknown reporting error");

export default function useReportingResource(
  loader,
  {
    key,
    refreshToken = 0,
    enabled = true,
    initialData = null,
  },
) {
  const loaderRef = React.useRef(loader);
  const requestSequence = React.useRef(0);
  const [localRefresh, setLocalRefresh] = React.useState(0);
  const [data, setData] = React.useState(initialData);
  const [loading, setLoading] = React.useState(enabled);
  const [error, setError] = React.useState("");
  const [hasLoaded, setHasLoaded] = React.useState(false);

  loaderRef.current = loader;

  const reload = React.useCallback(() => {
    setLocalRefresh((value) => value + 1);
  }, []);

  React.useEffect(() => {
    const sequence = ++requestSequence.current;

    if (!enabled) {
      setLoading(false);
      setError("");
      setData(initialData);
      setHasLoaded(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError("");

    Promise.resolve()
      .then(() => loaderRef.current())
      .then((result) => {
        if (!active || sequence !== requestSequence.current) return;
        setData(result ?? initialData);
        setHasLoaded(true);
      })
      .catch((reason) => {
        if (!active || sequence !== requestSequence.current) return;
        setError(errorMessage(reason));
        setHasLoaded(true);
      })
      .finally(() => {
        if (!active || sequence !== requestSequence.current) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [key, refreshToken, localRefresh, enabled]); // loader is held in loaderRef intentionally

  return {
    data,
    loading,
    error,
    hasLoaded,
    reload,
  };
}
